import React from 'react';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';

import backend from 'utils/backend';
import {Box, Button, Stack} from 'views/components';
import {Translate} from 'views/module';
import MarkdownViewer from 'views/module/markdown/MarkdownViewer';

// Sent to the agent's own session to greenlight the action it proposed, rather than re-stating it.
const APPROVAL_MESSAGE = 'Approved. Please proceed with exactly what you described above.';

const AgentPromptResponseView = (props) => {
  const {onApproveAndSendSuccess, onDiscard, response} = props;
  const navigate = useNavigate();
  const [isApproving, setIsApproving] = React.useState(false);
  const [approvedMessage, setApprovedMessage] = React.useState(null);
  const isApproved = approvedMessage !== null;
  const displayedMessage = approvedMessage ?? response.message ?? '';

  const handleApprove = React.useCallback(async () => {
    if (!response.sessionRn || isApproving) {
      return;
    }

    setIsApproving(true);
    try {
      const result = await backend.agentChat.sendMessage(APPROVAL_MESSAGE, response.sessionRn);
      setApprovedMessage(result?.assistantMessage?.content || '');
      await onApproveAndSendSuccess?.();
    } finally {
      setIsApproving(false);
    }
  }, [isApproving, onApproveAndSendSuccess, response.sessionRn]);

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #CDD7FF',
        borderRadius: '16px',
        boxShadow: '0px 1px 2px rgba(130, 145, 255, 0.06)',
      }}
    >
      <Stack
        spacing={1}
        sx={{
          px: '18px',
          py: '12px',
        }}
      >
        <Box sx={{maxHeight: '280px', overflowY: 'auto'}}>
          <MarkdownViewer value={displayedMessage} />
        </Box>
        {
          response.agentJobRn &&
            <Box sx={{fontSize: '11px', lineHeight: '150%', color: '#A8A49E'}}>
              {response.agentJobRn}
            </Box>
        }
        <Stack direction='row' spacing={1} sx={{flexWrap: 'wrap', justifyContent: 'flex-end'}}>
          {
            !isApproved &&
              // Same primary style DraftMessage uses for Email/Sms's "Approve & send"
              // (views/module/correspondances/DraftMessage.jsx).
              <Button
                disableElevation
                disabled={!response.sessionRn || isApproving}
                onClick={handleApprove}
                sx={{
                  'px': '10px',
                  'py': '8px',
                  'borderRadius': '7px',
                  'fontSize': '11px',
                  'fontWeight': 400,
                  'lineHeight': '100%',
                  'textTransform': 'none',
                  'color': '#FFFFFF',
                  'backgroundColor': '#E4875B',
                  '&:hover': {
                    backgroundColor: '#DB7D51',
                  },
                }}
              >
                <Translate expression='skills.SkillsModule.AgentPrompt.Actions.Approve' />
              </Button>
          }
          {
            response.sessionRn &&
              // Same secondary (outlined) style DraftMessage uses for "Regenerate".
              <Button
                variant='outlined'
                onClick={() => navigate(`/agent/chat?sessionRn=${response.sessionRn}`)}
                sx={{
                  'height': '42px',
                  'textTransform': 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <Translate expression='skills.SkillsModule.AgentPrompt.Actions.ViewSession' />
              </Button>
          }
          <Button
            variant='text'
            onClick={onDiscard}
            sx={{
              'px': '4px',
              'py': '10px',
              'borderRadius': '7px',
              'fontSize': '11px',
              'fontWeight': 400,
              'lineHeight': '100%',
              'textTransform': 'none',
              'color': '#6F6A62',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#1A1916',
              },
            }}
          >
            <Translate expression='skills.SkillsModule.AgentPrompt.Actions.Discard' />
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

AgentPromptResponseView.propTypes = {
  onApproveAndSendSuccess: PropTypes.func,
  onDiscard: PropTypes.func,
  response: PropTypes.shape({
    agentJobRn: PropTypes.string,
    message: PropTypes.string,
    outputType: PropTypes.string,
    sessionRn: PropTypes.string,
    status: PropTypes.string,
  }),
};

export default React.memo(AgentPromptResponseView);
